import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, ArrowUp, Instagram, Facebook, Youtube, Zap } from 'lucide-react';
import { companyInfo } from '../mockData';

const nav = [
  { name: 'Accueil', path: '/' },
  { name: 'TSE OUEST', path: '/about' },
  { name: 'Nos Services', path: '/services' },
  { name: 'Nos Activités', path: '/activities' },
  { name: 'Actualités', path: '/blog' },
  { name: 'Galerie Photo', path: '/portfolio' },
  { name: 'Prenez rendez-vous', path: '/contact' }
];

const socials = [
  { icon: Instagram, href: companyInfo.socialMedia.instagram, label: 'Instagram', id: 'instagram' },
  { icon: Facebook, href: companyInfo.socialMedia.facebook, label: 'Facebook', id: 'facebook' },
  { icon: Youtube, href: companyInfo.socialMedia.youtube, label: 'YouTube', id: 'youtube' }
];

const Footer = () => {
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 600);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <footer className="relative bg-[#050811] text-slate-400 pt-20 pb-10 border-t border-white/10 overflow-hidden" data-testid="footer">
      <div className="absolute inset-0 circuit-grid opacity-40" />
      <div className="glow-orb w-[500px] h-[300px] bg-energy/10 -top-40 right-0" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-5">
            <div className="bg-white rounded-xl px-4 py-3 inline-block shadow-[0_0_0_1px_rgba(255,255,255,0.12)]">
              <img src="/assets/tse-ouest-logo-transparent.png" alt="TSE OUEST" className="h-16 w-auto" />
            </div>
            <p className="mt-6 text-base text-slate-300 max-w-md leading-relaxed">
              Électricité haute précision & plâtrerie architecturale. Nous intervenons pour particuliers
              et professionnels à Angers et dans tout le Maine-et-Loire.
            </p>
            <div className="mt-6 inline-flex items-center gap-3 rounded-full border border-signal/30 bg-signal/5 px-4 py-2">
              <span className="live-dot w-2 h-2 rounded-full bg-signal" />
              <span className="font-mono text-xs uppercase tracking-[0.18em] text-signal">
                Équipes opérationnelles en Anjou (49)
              </span>
            </div>
            <div className="flex gap-3 mt-8">
              {socials.map(({ icon: Icon, href, label, id }) => (
                <a
                  key={id}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={`footer-social-${id}`}
                  className="w-11 h-11 rounded-xl border border-white/10 bg-white/5 flex items-center justify-center text-slate-300 hover:text-white hover:border-energy hover:bg-energy/20 hover:shadow-[0_0_20px_rgba(255,94,20,0.4)] transition-all duration-300 hover:-translate-y-1"
                >
                  <Icon size={18} />
                </a>
              ))}
            </div>
          </div>

          <div className="lg:col-span-3">
            <h4 className="font-mono text-xs uppercase tracking-[0.25em] text-amber mb-6">Navigation</h4>
            <ul className="space-y-3">
              {nav.map((l) => (
                <li key={l.path}>
                  <Link to={l.path} className="text-sm text-slate-300 hover:text-energy transition-colors inline-flex items-center gap-2 group">
                    <span className="w-0 group-hover:w-3 h-px bg-energy transition-all duration-300" />
                    {l.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <h4 className="font-mono text-xs uppercase tracking-[0.25em] text-amber mb-6">Contact</h4>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <MapPin size={18} className="text-energy mt-0.5 shrink-0" />
                <p className="text-sm text-slate-300">{companyInfo.address}</p>
              </div>
              <a href={`tel:${companyInfo.phone}`} data-testid="footer-phone-link" className="flex items-center gap-3 text-sm text-slate-300 hover:text-signal transition-colors">
                <Phone size={18} className="text-energy shrink-0" />
                <span className="font-mono">06 99 13 45 11</span>
              </a>
              <a href={`mailto:${companyInfo.email}`} data-testid="footer-email-link" className="flex items-center gap-3 text-sm text-slate-300 hover:text-signal transition-colors">
                <Mail size={18} className="text-energy shrink-0" />
                {companyInfo.email}
              </a>
            </div>
            <Link
              to="/contact"
              data-testid="footer-contact-cta"
              className="btn-energy mt-8 inline-flex items-center gap-2 font-bold text-white px-6 py-3 rounded-xl"
            >
              <Zap size={16} /> Devis gratuit
            </Link>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col lg:flex-row gap-6 lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-3">
            <span className="font-mono text-[11px] uppercase tracking-wider px-3 py-1.5 rounded-md border border-white/10 bg-white/5 text-slate-400">
              SIRET {companyInfo.siret}
            </span>
            <span className="font-mono text-[11px] uppercase tracking-wider px-3 py-1.5 rounded-md border border-white/10 bg-white/5 text-slate-400">
              TVA {companyInfo.tva}
            </span>
            <span className="font-mono text-[11px] uppercase tracking-wider px-3 py-1.5 rounded-md border border-energy/30 bg-energy/10 text-amber">
              NF C 15-100
            </span>
          </div>
          <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-1">
            <span>© TSE OUEST {new Date().getFullYear()} — Tous droits réservés</span>
            <span className="hidden sm:inline">•</span>
            <Link to="/mentions-legales" className="hover:text-energy transition-colors">Mentions légales</Link>
            <span className="hidden sm:inline">•</span>
            <button className="hover:text-energy transition-colors">Gestion des cookies</button>
          </div>
        </div>
      </div>

      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label="Retour en haut"
        data-testid="scroll-top-btn"
        className={`fixed bottom-6 right-6 z-40 w-12 h-12 rounded-xl bg-abyss/80 backdrop-blur-xl border border-white/15 text-white flex items-center justify-center hover:border-energy hover:shadow-[0_0_20px_rgba(255,94,20,0.5)] transition-all duration-300 ${
          showTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
      >
        <ArrowUp size={20} />
      </button>
    </footer>
  );
};

export default Footer;
